import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { ArrowLeft, Plus, Minus, Search } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const FAQPage = () => {
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const allFaqs = [
    { qKey: 'faq_1_q', aKey: 'faq_1_a', category: 'verifactu' },
    { qKey: 'faq_2_q', aKey: 'faq_2_a', category: 'general' },
    { qKey: 'faq_3_q', aKey: 'faq_3_a', category: 'technical' },
    { qKey: 'faq_4_q', aKey: 'faq_4_a', category: 'technical' },
    { qKey: 'faq_5_q', aKey: 'faq_5_a', category: 'billing' },
    // Additional FAQs
    {
      q: language === 'es' ? '¿Cómo funciona el escáner de código de barras?' : 'How does the barcode scanner work?',
      a: language === 'es' 
        ? 'VendFactura soporta escáneres USB (que envían pulsaciones de tecla) y también puede usar la cámara de tu dispositivo para escanear códigos. Los productos se añaden automáticamente al ticket.'
        : 'VendFactura supports USB scanners (that send keystrokes) and can also use your device\'s camera to scan codes. Products are automatically added to the ticket.',
      category: 'technical',
      isStatic: true
    },
    {
      q: language === 'es' ? '¿Puedo usar VendFactura en varios dispositivos?' : 'Can I use VendFactura on multiple devices?',
      a: language === 'es'
        ? 'Sí, puedes acceder a VendFactura desde cualquier dispositivo con navegador web. También tenemos apps móviles para iOS y Android.'
        : 'Yes, you can access VendFactura from any device with a web browser. We also have mobile apps for iOS and Android.',
      category: 'general',
      isStatic: true
    },
    {
      q: language === 'es' ? '¿Qué pasa si pierdo mi conexión a internet?' : 'What happens if I lose my internet connection?',
      a: language === 'es'
        ? 'El modo offline te permite seguir registrando ventas sin conexión. Cuando recuperes internet, todos los datos se sincronizan automáticamente con nuestros servidores.'
        : 'Offline mode allows you to continue recording sales without connection. When you regain internet, all data syncs automatically with our servers.',
      category: 'technical',
      isStatic: true
    },
    {
      q: language === 'es' ? '¿Cómo configuro una impresora térmica?' : 'How do I set up a thermal printer?',
      a: language === 'es'
        ? 'Ve a Configuración > Impresora en tu panel de control. Soportamos conexiones Bluetooth, USB y WiFi. La mayoría de impresoras ESC/POS son compatibles.'
        : 'Go to Settings > Printer in your dashboard. We support Bluetooth, USB and WiFi connections. Most ESC/POS printers are compatible.',
      category: 'technical',
      isStatic: true
    },
    {
      q: language === 'es' ? '¿Cómo añado empleados con acceso PIN?' : 'How do I add employees with PIN access?',
      a: language === 'es'
        ? 'En el plan TPV, ve a Personal > Añadir empleado. Asigna un rol (cajero, encargado, etc.) y el sistema generará un PIN de 4 dígitos único para cada empleado.'
        : 'In the POS plan, go to Staff > Add employee. Assign a role (cashier, manager, etc.) and the system will generate a unique 4-digit PIN for each employee.',
      category: 'general',
      isStatic: true
    }
  ];

  const filteredFaqs = allFaqs.filter(faq => {
    const question = faq.isStatic ? faq.q : t(faq.qKey);
    const answer = faq.isStatic ? faq.a : t(faq.aKey);
    const query = searchQuery.toLowerCase();
    return question.toLowerCase().includes(query) || answer.toLowerCase().includes(query);
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="faq-page">
      <Navbar />
      
      <main className="vf-section">
        <div className="vf-container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>

          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h1 className="vf-heading-1 text-[#1e3a5f] mb-4">
                {t('faq_title')}
              </h1>
              <p className="vf-body-large text-slate-600">
                {language === 'es' 
                  ? 'Todo lo que necesitas saber sobre VendFactura'
                  : 'Everything you need to know about VendFactura'}
              </p>
            </motion.div>

            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative mb-8"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'es' ? 'Buscar preguntas...' : 'Search questions...'}
                className="vf-input w-full pl-12 pr-4 py-4"
                data-testid="faq-search-input"
              />
            </motion.div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => {
                const question = faq.isStatic ? faq.q : t(faq.qKey);
                const answer = faq.isStatic ? faq.a : t(faq.aKey);
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border-2 border-slate-100 overflow-hidden"
                    data-testid={`faq-page-item-${index + 1}`}
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-semibold text-[#1e3a5f] pr-4">
                        {question}
                      </span>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-sm flex items-center justify-center transition-colors ${
                        openIndex === index ? 'bg-[#22c55e]' : 'bg-slate-100'
                      }`}>
                        {openIndex === index ? (
                          <Minus className={`w-5 h-5 ${openIndex === index ? 'text-white' : 'text-[#1e3a5f]'}`} />
                        ) : (
                          <Plus className={`w-5 h-5 ${openIndex === index ? 'text-white' : 'text-[#1e3a5f]'}`} />
                        )}
                      </div>
                    </button>
                    
                    <AnimatePresence>
                      {openIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="px-6 pb-5 text-slate-600 border-t border-slate-100 pt-4">
                            {answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* No Results */}
            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">
                  {language === 'es' 
                    ? 'No se encontraron preguntas. Prueba con otra búsqueda.'
                    : 'No questions found. Try another search.'}
                </p>
              </div>
            )}

            {/* Still have questions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-16 text-center bg-[#1e3a5f] text-white p-10"
            >
              <h3 className="text-2xl font-bold mb-4">
                {language === 'es' ? '¿Tienes más preguntas?' : 'Still have questions?'}
              </h3>
              <p className="text-white/80 mb-6">
                {language === 'es'
                  ? 'Nuestro equipo está listo para ayudarte'
                  : 'Our team is ready to help you'}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#22c55e] text-white px-8 py-4 font-bold hover:bg-[#16a34a] transition-colors"
                data-testid="faq-contact-button"
              >
                {language === 'es' ? 'Contactar Soporte' : 'Contact Support'}
              </Link>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
